import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  Plus, Edit, Trash2, User, Phone, Mail, Car, UserCircle2,
  Eye, EyeOff, Shield, Search
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DriversManagement = ({ getAuthHeaders }) => {
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    mobile: '',
    email: '',
    username: '',
    password: '',
    role: 'driver',
    vehicle_type: '',
    vehicle_reg: '',
    vehicle_colour: '',
    is_active: true
  });

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/drivers`, getAuthHeaders());
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (driver = null) => {
    if (driver) {
      setEditingDriver(driver);
      setFormData({
        first_name: driver.first_name,
        last_name: driver.last_name,
        mobile: driver.mobile,
        email: driver.email || '',
        username: driver.username,
        password: '',
        role: driver.role,
        vehicle_type: driver.vehicle_type || '',
        vehicle_reg: driver.vehicle_reg || '',
        vehicle_colour: driver.vehicle_colour || '',
        is_active: driver.is_active
      });
    } else {
      setEditingDriver(null);
      setFormData({
        first_name: '',
        last_name: '',
        mobile: '',
        email: '',
        username: '',
        password: '',
        role: 'driver',
        vehicle_type: '',
        vehicle_reg: '',
        vehicle_colour: '',
        is_active: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingDriver(null);
    setShowPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.first_name || !formData.last_name || !formData.mobile || !formData.username) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!editingDriver && !formData.password) {
      toast.error('Password is required for new drivers');
      return;
    }

    try {
      // Clean up form data - remove empty password for updates
      const submitData = { ...formData };
      if (editingDriver && !submitData.password) {
        delete submitData.password;
      }

      if (editingDriver) {
        await axios.put(
          `${API}/drivers/${editingDriver.id}`,
          submitData,
          getAuthHeaders()
        );
        toast.success('Driver updated successfully');
      } else {
        await axios.post(
          `${API}/drivers`,
          submitData,
          getAuthHeaders()
        );
        toast.success('Driver created successfully');
      }
      
      handleCloseDialog();
      fetchDrivers();
    } catch (error) {
      console.error('Failed to save driver:', error);
      toast.error(error.response?.data?.detail || 'Failed to save driver');
    }
  };

  const handleDelete = async (driverId) => {
    if (!window.confirm('Are you sure you want to delete this driver? This will deactivate their account.')) {
      return;
    }

    try {
      await axios.delete(`${API}/drivers/${driverId}`, getAuthHeaders());
      toast.success('Driver deleted successfully');
      fetchDrivers();
    } catch (error) {
      console.error('Failed to delete driver:', error);
      toast.error('Failed to delete driver');
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const searchLower = searchTerm.toLowerCase();
    return (
      driver.first_name.toLowerCase().includes(searchLower) ||
      driver.last_name.toLowerCase().includes(searchLower) ||
      driver.username.toLowerCase().includes(searchLower) ||
      driver.mobile.includes(searchTerm) ||
      (driver.vehicle_reg && driver.vehicle_reg.toLowerCase().includes(searchLower))
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Driver Management</h1>
          <p className="text-gray-500 mt-1">Manage your mobile workforce</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Driver
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search drivers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Drivers</p>
              <p className="text-2xl font-bold">{drivers.filter(d => d.role === 'driver').length}</p>
            </div>
            <User className="w-8 h-8 text-blue-500" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Supervisors</p>
              <p className="text-2xl font-bold">{drivers.filter(d => d.role === 'supervisor').length}</p>
            </div>
            <Shield className="w-8 h-8 text-green-500" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold">{drivers.filter(d => d.is_active).length}</p>
            </div>
            <UserCircle2 className="w-8 h-8 text-teal-500" />
          </div>
        </Card>
      </div>

      {/* Drivers Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Driver
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDrivers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No drivers found. Click "Add Driver" to create one.
                  </td>
                </tr>
              ) : (
                filteredDrivers.map((driver) => (
                  <tr key={driver.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center text-white font-semibold">
                          {driver.first_name[0]}{driver.last_name[0]}
                        </div>
                        <div className="ml-3">
                          <div className="font-medium text-gray-900">
                            {driver.first_name} {driver.last_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 flex items-center gap-1">
                        <Phone className="w-3 h-3 text-gray-400" />
                        {driver.mobile}
                      </div>
                      {driver.email && (
                        <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <Mail className="w-3 h-3 text-gray-400" />
                          {driver.email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {driver.username}
                    </td>
                    <td className="px-6 py-4">
                      {driver.vehicle_reg ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                            <Car className="w-3 h-3 text-gray-400" />
                            {driver.vehicle_reg}
                          </div>
                          <div className="text-xs text-gray-500">
                            {driver.vehicle_type && `${driver.vehicle_type}`}
                            {driver.vehicle_colour && ` • ${driver.vehicle_colour}`}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">No vehicle</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        driver.role === 'supervisor' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {driver.role === 'supervisor' ? (
                          <>
                            <Shield className="w-3 h-3 mr-1" />
                            Supervisor
                          </>
                        ) : (
                          <>
                            <User className="w-3 h-3 mr-1" />
                            Driver
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        driver.is_active 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {driver.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(driver)}
                          className="gap-1"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(driver.id)}
                          className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Driver Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDriver ? 'Edit Driver' : 'Add New Driver'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Personal Information */}
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="mobile">Mobile *</Label>
                <Input
                  id="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              {/* Login Credentials */}
              <div>
                <Label htmlFor="username">Username *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">
                  Password {!editingDriver && '*'}
                  {editingDriver && <span className="text-xs text-gray-500 ml-1">(leave blank to keep current)</span>}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingDriver}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Role */}
              <div className="col-span-2">
                <Label htmlFor="role">Role *</Label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="driver">Driver</option>
                  <option value="supervisor">Supervisor</option>
                </select>
              </div>

              {/* Vehicle Information */}
              <div>
                <Label htmlFor="vehicle_type">Vehicle Type</Label>
                <Input
                  id="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                  placeholder="e.g., Van, Car"
                />
              </div>
              <div>
                <Label htmlFor="vehicle_reg">Vehicle Registration</Label>
                <Input
                  id="vehicle_reg"
                  value={formData.vehicle_reg}
                  onChange={(e) => setFormData({ ...formData, vehicle_reg: e.target.value.toUpperCase() })}
                  placeholder="e.g., AB12 CDE"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="vehicle_colour">Vehicle Colour</Label>
                <Input
                  id="vehicle_colour"
                  value={formData.vehicle_colour}
                  onChange={(e) => setFormData({ ...formData, vehicle_colour: e.target.value })}
                  placeholder="e.g., White, Blue"
                />
              </div>

              {/* Status */}
              {editingDriver && (
                <div className="col-span-2">
                  <Label htmlFor="is_active" className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Active Account
                  </Label>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">
                {editingDriver ? 'Update Driver' : 'Create Driver'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriversManagement;
